import("../css/accordion.scss");

const classNames = Object.freeze({
	collapsed: "rt-accordion-collapsed",
	alreadyInstantiated: "rt-accordion-on",
	open: "rt-accordion-open",
	arrow: "rt-accordion-arrow",
});

const selectors = Object.freeze({
	button: ".rt-accordion-button",
	container: ".rt-accordion-container",
	body: ".rt-accordion-body",
	arrow: ".rt-accordion-arrow",
	main: "#content-area",
});

/** Watch for accordion containers and add toggle functionality */
export function initializeAccordions(): void {
	setupPage(findContainers());
	const observerOptions = {
		childList: true,
		subtree: true,
	};
	const elementToWatch = document.querySelector(selectors.main);
	const observer = new MutationObserver((e) => handleMutation(e));
	if (!elementToWatch) {
		return;
	}
	observer.observe(elementToWatch, observerOptions);
}

/**
 * Watch for changes in the main content area and reinitialize the accordions
 *
 * @param e The mutation record
 */
function handleMutation(e: MutationRecord[]): void {
	if (e.some((mutation) => mutation.type === "childList")) {
		setupPage(findContainers());
	}
}
/**
 * Set up the page for accordion functionality
 *
 * @param containers The containers to set up
 */
function setupPage(containers: HTMLElement[]) {
	for (const container of containers) {
		prepareToggle(container);
	}
}

/**
 * Find all accordion containers in the document
 *
 * @returns The containers
 */
function findContainers(): HTMLDivElement[] {
	return Array.from(document.querySelectorAll(selectors.container));
}

/**
 * Determine if the container should be excluded from the accordion
 *
 * @param container The container to check
 * @returns True if the container should be excluded
 */
function containerIsExcluded(container: HTMLElement) {
	return container?.dataset?.collapse;
}

/**
 * Prepare the container for toggling
 *
 * @param container The container to prepare
 */
function prepareToggle(container: HTMLElement) {
	if (
		isContainerAlreadyInstantiated(container) ||
		containerIsExcluded(container)
	)
		return;
	container.classList.add(classNames.alreadyInstantiated);
	const toggleButton = container.querySelector(selectors.button);
	if (!toggleButton) return;
	addArrowToToggle(container, toggleButton);
	toggleButton.addEventListener("click", () => handleToggle(container));
}

/**
 * Determine if the container has already been instantiated
 *
 * @param container The container to check
 * @returns True if the container has already been instantiated
 */
function isContainerAlreadyInstantiated(container: HTMLElement): boolean {
	return container.classList.contains(classNames.alreadyInstantiated);
}

/**
 * Adds the arrow to the toggle button
 *
 * @param container The container
 * @param toggleButton The toggle button
 */
function addArrowToToggle(container: Element, toggleButton: Element) {
	const correspondingBody = container.querySelector(selectors.body);
	if (!correspondingBody) return;
	const toggleAdditionalClasses = correspondingBody.classList.contains(
		classNames.collapsed,
	)
		? classNames.collapsed
		: undefined;
	const arrowNode = createArrowNode(toggleAdditionalClasses);
	toggleButton.append(arrowNode);
}

/**
 * Handles the toggle event
 *
 * @param container The container to toggle
 */
function handleToggle(container: HTMLElement): void {
	const button = container.querySelector(selectors.button);
	const body = container.querySelector(selectors.body);
	const toggleArrow = container.querySelector(selectors.arrow);
	if (!button || !body || !toggleArrow) return;
	button.classList.toggle(classNames.open);
	toggleArrow.classList.toggle(classNames.collapsed);
	body.classList.toggle(classNames.collapsed);
}

/**
 * Create the arrow icon node
 *
 * @param additionalClass Any additional classes to add to the arrow
 * @returns The arrow node
 */
function createArrowNode(additionalClass?: string): ChildNode {
	const template = document.createElement("template");
	template.innerHTML = `<i class="fa-solid fa-angle-up ${classNames.arrow}"></i>`;
	const svgNode = template.content.firstChild as HTMLElement;
	if (additionalClass) {
		svgNode.classList.add(additionalClass);
	}
	return svgNode;
}
